import { useState } from 'react';
import { Download, Plus, Receipt, Trash2, Undo2, Wallet, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, SkeletonTable, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, useToast } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { FilterChips } from '@/components/ui/filter-chips';
import { SidePanel } from '@/components/ui/side-panel';
import { useAuth } from '@/hooks/useAuth';
import { useSuppliers } from '@/hooks/useSuppliers';
import {
  usePayables,
  usePayableDetail,
  PAYABLE_STATUS_LABELS,
  PAYABLE_METHOD_LABELS,
  type Payable,
  type PayableFilter,
  type PayableMethod,
  type PayableStatus,
} from '@/hooks/usePayables';
import { api } from '@/lib/api';
import { formatReais } from '@/lib/utils';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { formatApiError, formatDateBR, todayIsoIn } from '@mustauto/utils';
import { useCan } from '@/hooks/useCan';

/** Esta tela já formatava data pura sem `new Date()` — e era a única. As
 *  cópias locais viraram `formatDateBR`/`todayIsoIn` em `@mustauto/utils`,
 *  onde a OC e os gráficos também leem: a regra vale para todo campo que é um
 *  dia, não só para o vencimento que alguém lembrou de proteger aqui. */
function formatDueDate(iso: string | null): string {
  return iso ? formatDateBR(iso) : '—';
}

const STATUS_VARIANT: Record<PayableStatus, 'success' | 'destructive' | 'warning' | 'secondary'> = {
  paid: 'success',
  overdue: 'destructive',
  partial: 'warning',
  open: 'secondary',
};

function PayableStatusBadge({ status }: { status: PayableStatus }) {
  return <Badge variant={STATUS_VARIANT[status]}>{PAYABLE_STATUS_LABELS[status]}</Badge>;
}

const FILTERS: { key: PayableFilter; label: string }[] = [
  { key: 'open', label: 'Em aberto' },
  { key: 'overdue', label: 'Vencidos' },
  { key: 'paid', label: 'Pagos' },
];

export function PayablesSection() {
  const { toast } = useToast();
  // A sessão demo é read-only por construção (deny-by-default na API); esconder
  // as ações evita oferecer um botão que só sabe devolver 403.
  const { isDemo } = useAuth();
  // Lançar, pagar e excluir título é `financeiro.lancar` (Fase 3); ver é `financeiro.ver`.
  const canWrite = useCan()('financeiro.lancar');
  const readOnly = isDemo || !canWrite;
  const [filter, setFilter] = useState<PayableFilter>('open');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Payable | null>(null);
  const [paying, setPaying] = useState<Payable | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const {
    payables,
    openTotal,
    overdueTotal,
    isLoading,
    isError,
    error,
    refetch,
    summary,
    create,
    update,
    remove,
    pay,
    isCreating,
    isUpdating,
    isPaying,
  } = usePayables({ status: filter });

  async function handleExport() {
    setExporting(true);
    try {
      const res = await api.get('/workshops/my/finance/payables/export', {
        params: { status: filter },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `mustauto-contas-a-pagar-${todayIsoIn()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        type: 'error',
        title: 'Falha ao exportar contas a pagar',
        description: formatApiError(err, 'Não foi possível gerar o arquivo. Tente novamente.'),
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-base font-semibold text-foreground">
            Contas a Pagar
          </CardTitle>
          {summary && summary.openTotal > 0 && (
            <span className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{formatReais(summary.openTotal)}</span>{' '}
              em aberto
              {summary.overdueTotal > 0 && (
                <>
                  {' · '}
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {formatReais(summary.overdueTotal)} vencido
                  </span>
                </>
              )}
              {summary.dueIn7DaysTotal > 0 && (
                <> · {formatReais(summary.dueIn7DaysTotal)} vence em 7 dias</>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            <Download size={14} />
            {exporting ? 'Exportando…' : 'CSV'}
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              <Plus size={14} />
              Novo lançamento
            </button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <FilterChips
          chips={FILTERS.map((f) => ({ key: f.key, label: f.label }))}
          selected={filter}
          onSelect={(k) => setFilter(k as PayableFilter)}
        />

        {isLoading ? (
          <SkeletonTable rows={3} />
        ) : isError && payables.length === 0 ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : payables.length === 0 ? (
          <EmptyState
            icon={<Wallet size={28} />}
            title={
              filter === 'paid'
                ? 'Nenhum título pago no período'
                : filter === 'overdue'
                  ? 'Nenhum título vencido'
                  : 'Nenhuma conta a pagar'
            }
            description={
              filter === 'open'
                ? 'Lance uma compra de balcão aqui, ou confirme uma ordem de compra — ela vira título automaticamente.'
                : 'Troque o filtro para ver os outros títulos.'
            }
            action={
              filter === 'open' && !readOnly
                ? { label: 'Novo lançamento', onClick: () => { setEditing(null); setShowForm(true); } }
                : undefined
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Em aberto</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {payables.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setDetailId(p.id)}
                  >
                    <TableCell className="font-medium">{p.supplierName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.description}
                      {p.purchaseOrderNumber != null && (
                        <span className="ml-1 text-xs">· OC #{p.purchaseOrderNumber}</span>
                      )}
                    </TableCell>
                    <TableCell
                      className={`text-sm ${p.status === 'overdue' ? 'font-semibold text-red-600 dark:text-red-400' : ''}`}
                    >
                      {formatDueDate(p.dueDate)}
                    </TableCell>
                    <TableCell className="text-right">{formatReais(p.amount)}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatReais(p.remaining)}
                    </TableCell>
                    <TableCell className="text-center">
                      <PayableStatusBadge status={p.status} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {p.status !== 'paid' && !readOnly && (
                          <button
                            type="button"
                            onClick={() => setPaying(p)}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                          >
                            Pagar
                          </button>
                        )}
                        {!readOnly && (
                        <button
                          type="button"
                          aria-label={`Excluir título ${p.description}`}
                          onClick={async () => {
                            try {
                              await remove(p.id);
                            } catch (err) {
                              toast({
                                type: 'error',
                                title: 'Falha ao excluir título',
                                description: formatApiError(err, 'Tente novamente.'),
                              });
                            }
                          }}
                          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-red-600 dark:hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex justify-end gap-6 border-t border-border pt-3 text-sm">
              <span className="text-muted-foreground">
                Em aberto neste filtro:{' '}
                <span className="font-semibold text-foreground">{formatReais(openTotal)}</span>
              </span>
              {overdueTotal > 0 && (
                <span className="font-semibold text-red-600 dark:text-red-400">
                  Vencido: {formatReais(overdueTotal)}
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>

      {showForm && (
        <PayableFormModal
          payable={editing}
          isSubmitting={isCreating || isUpdating}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSubmit={async (data) => {
            try {
              if (editing) {
                await update({ id: editing.id, ...data });
              } else {
                await create(data);
              }
              setShowForm(false);
              setEditing(null);
            } catch (err) {
              toast({
                type: 'error',
                title: 'Falha ao salvar título',
                description: formatApiError(err, 'Confira os dados e tente novamente.'),
              });
            }
          }}
        />
      )}

      {paying && (
        <PayModal
          payable={paying}
          isSubmitting={isPaying}
          onClose={() => setPaying(null)}
          onSubmit={async (data) => {
            try {
              await pay({ id: paying.id, ...data });
              setPaying(null);
            } catch (err) {
              toast({
                type: 'error',
                title: 'Falha ao registrar pagamento',
                description: formatApiError(err, 'Confira o valor e tente novamente.'),
              });
            }
          }}
        />
      )}

      {detailId && (
        <PayableDetailPanel
          payableId={detailId}
          onClose={() => setDetailId(null)}
          onEdit={(p) => { setDetailId(null); setEditing(p); setShowForm(true); }}
          onPay={(p) => { setDetailId(null); setPaying(p); }}
        />
      )}
    </Card>
  );
}

// ── Modal: novo / editar título ──────────────────────────────

function PayableFormModal({
  payable,
  onSubmit,
  onClose,
  isSubmitting,
}: {
  payable: Payable | null;
  onSubmit: (data: {
    supplierId: string;
    description: string;
    amount: number;
    dueDate?: string | null;
    notes?: string | null;
  }) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const {
    suppliers,
    isLoading: loadingSuppliers,
    create: createSupplier,
    isCreating: creatingSupplier,
  } = useSuppliers();
  const activeSuppliers = suppliers.filter((s) => s.active || s.id === payable?.supplierId);

  // Cadastro de fornecedor sem sair do lançamento. O formulário exigia um
  // fornecedor e, quando não havia nenhum, mandava o gestor para outra tela —
  // perdendo tudo que ele já tinha digitado aqui. Só o nome é obrigatório na
  // API; o resto do cadastro continua em Estoque → Fornecedores.
  const [newSupplierName, setNewSupplierName] = useState<string | null>(null);
  const [supplierError, setSupplierError] = useState<string | null>(null);

  async function handleCreateSupplier() {
    const name = newSupplierName?.trim();
    if (!name || creatingSupplier) return;
    setSupplierError(null);
    try {
      const created = await createSupplier({ name });
      // Selecionar o recém-criado é o ponto do atalho: sem isso o gestor
      // ainda teria que procurá-lo na lista.
      setSupplierId(created.id);
      setNewSupplierName(null);
    } catch (err) {
      setSupplierError(formatApiError(err, 'Não foi possível cadastrar o fornecedor.'));
    }
  }

  const [supplierId, setSupplierId] = useState(payable?.supplierId ?? '');
  const [description, setDescription] = useState(payable?.description ?? '');
  const [amount, setAmount] = useState(payable ? String(payable.amount) : '');
  const [dueDate, setDueDate] = useState(payable?.dueDate ?? '');
  const [notes, setNotes] = useState(payable?.notes ?? '');

  const parsedAmount = Number(amount.replace(',', '.'));
  const canSubmit =
    !!supplierId && description.trim().length > 0 && parsedAmount > 0 && !isSubmitting;

  return (
    <div
      ref={trapRef}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold">
              {payable ? 'Editar título' : 'Novo lançamento a pagar'}
            </h3>
            <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted">
              <X size={18} />
            </button>
          </div>

          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!canSubmit) return;
              await onSubmit({
                supplierId,
                description: description.trim(),
                amount: parsedAmount,
                dueDate: dueDate || null,
                notes: notes.trim() || null,
              });
            }}
          >
            <div className="flex flex-col gap-1">
              <label htmlFor="payable-supplier" className="text-xs font-medium text-muted-foreground">
                Fornecedor *
              </label>
              <select
                id="payable-supplier"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
                disabled={loadingSuppliers}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">
                  {loadingSuppliers ? 'Carregando fornecedores…' : 'Selecione o fornecedor'}
                </option>
                {activeSuppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {/* Aviso inline sob o campo, não estado vazio de tela. */}
              {!loadingSuppliers && activeSuppliers.length === 0 && newSupplierName === null && (
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  Nenhum fornecedor cadastrado ainda.
                </span>
              )}

              {newSupplierName === null ? (
                <button
                  type="button"
                  onClick={() => { setSupplierError(null); setNewSupplierName(''); }}
                  className="flex w-fit items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <Plus size={12} /> Cadastrar fornecedor
                </button>
              ) : (
                <div className="mt-1 rounded-md border border-input p-3">
                  <label htmlFor="payable-new-supplier" className="text-xs font-medium text-muted-foreground">
                    Nome do fornecedor
                  </label>
                  <div className="mt-1 flex gap-2">
                    <input
                      id="payable-new-supplier"
                      type="text"
                      autoFocus
                      value={newSupplierName}
                      maxLength={200}
                      onChange={(e) => setNewSupplierName(e.target.value)}
                      // Enter aqui cadastra o fornecedor. Sem isto ele
                      // submeteria o formulário de fora, que ainda está sem
                      // fornecedor escolhido.
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleCreateSupplier(); }
                      }}
                      placeholder="Ex.: Auto Peças Silva"
                      className="min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleCreateSupplier}
                      disabled={!newSupplierName.trim() || creatingSupplier}
                      className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50"
                    >
                      {creatingSupplier ? 'Salvando…' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setNewSupplierName(null); setSupplierError(null); }}
                      className="rounded-md px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      Cancelar
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    CNPJ, telefone e condição de pagamento podem ser preenchidos depois em
                    Estoque → Fornecedores.
                  </p>
                  {supplierError && (
                    <p className="mt-2 text-xs text-destructive">{supplierError}</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="payable-description" className="text-xs font-medium text-muted-foreground">
                Descrição *
              </label>
              <input
                id="payable-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                maxLength={200}
                placeholder="Ex.: Pastilhas de freio — nota 4471"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label htmlFor="payable-amount" className="text-xs font-medium text-muted-foreground">
                  Valor (R$) *
                </label>
                <input
                  id="payable-amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
                  required
                  placeholder="0,00"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="payable-due" className="text-xs font-medium text-muted-foreground">
                  Vencimento
                </label>
                <input
                  id="payable-due"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="payable-notes" className="text-xs font-medium text-muted-foreground">
                Observações
              </label>
              <textarea
                id="payable-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Modal: baixa (parcial ou total) ──────────────────────────

const METHODS: PayableMethod[] = [
  'pix',
  'boleto',
  'cash',
  'bank_transfer',
  'credit_card',
  'debit_card',
  'other',
];

function PayModal({
  payable,
  onSubmit,
  onClose,
  isSubmitting,
}: {
  payable: Payable;
  onSubmit: (data: {
    amount: number;
    paidAt?: string;
    method?: PayableMethod | null;
    notes?: string | null;
  }) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [amount, setAmount] = useState(String(payable.remaining));
  const [paidAt, setPaidAt] = useState(todayIsoIn());
  const [method, setMethod] = useState<PayableMethod | ''>('');
  const [notes, setNotes] = useState('');

  const parsed = Number(amount.replace(',', '.'));
  const exceeds = parsed > payable.remaining + 0.005;
  const canSubmit = parsed > 0 && !exceeds && !isSubmitting;

  return (
    <div
      ref={trapRef}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-base font-semibold">Registrar pagamento</h3>
            <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted">
              <X size={18} />
            </button>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            {payable.supplierName} · {payable.description} · saldo de{' '}
            <span className="font-semibold text-foreground">{formatReais(payable.remaining)}</span>
          </p>

          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!canSubmit) return;
              await onSubmit({
                amount: parsed,
                paidAt,
                method: method || null,
                notes: notes.trim() || null,
              });
            }}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label htmlFor="pay-amount" className="text-xs font-medium text-muted-foreground">
                  Valor pago (R$) *
                </label>
                <input
                  id="pay-amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
                  required
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                {exceeds && (
                  <span className="text-xs text-red-600 dark:text-red-400">
                    Maior que o saldo em aberto ({formatReais(payable.remaining)}).
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="pay-date" className="text-xs font-medium text-muted-foreground">
                  Data do pagamento
                </label>
                <input
                  id="pay-date"
                  type="date"
                  value={paidAt}
                  onChange={(e) => setPaidAt(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="pay-method" className="text-xs font-medium text-muted-foreground">
                Forma de pagamento
              </label>
              <select
                id="pay-method"
                value={method}
                onChange={(e) => setMethod(e.target.value as PayableMethod | '')}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Não informar</option>
                {METHODS.map((m) => (
                  <option key={m} value={m}>{PAYABLE_METHOD_LABELS[m]}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="pay-notes" className="text-xs font-medium text-muted-foreground">
                Observações
              </label>
              <input
                id="pay-notes"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={200}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? 'Registrando…' : 'Registrar pagamento'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Painel: detalhe do título + extrato de baixas ────────────

function PayableDetailPanel({
  payableId,
  onClose,
  onEdit,
  onPay,
}: {
  payableId: string;
  onClose: () => void;
  onEdit: (p: Payable) => void;
  onPay: (p: Payable) => void;
}) {
  const { toast } = useToast();
  const { isDemo } = useAuth();
  // Lançar, pagar e excluir título é `financeiro.lancar` (Fase 3); ver é `financeiro.ver`.
  const canWrite = useCan()('financeiro.lancar');
  const readOnly = isDemo || !canWrite;
  const { data, isLoading, isError, error, refetch } = usePayableDetail(payableId);
  const { reversePayment, isReversing } = usePayables();

  return (
    <SidePanel open onClose={onClose} title="Título a pagar">
      {isLoading ? (
        <SkeletonTable rows={3} />
      ) : isError || !data ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <div className="space-y-5">
          <div>
            <div className="text-xs text-muted-foreground">Fornecedor</div>
            <div className="text-sm font-medium">{data.supplierName}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Descrição</div>
            <div className="text-sm font-medium">
              {data.description}
              {data.purchaseOrderNumber != null && (
                <span className="ml-1 text-xs text-muted-foreground">
                  · OC #{data.purchaseOrderNumber}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Valor</div>
              <div className="text-base font-bold">{formatReais(data.amount)}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Pago</div>
              <div className="text-base font-bold">{formatReais(data.paidAmount)}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Em aberto</div>
              <div className="text-base font-bold">{formatReais(data.remaining)}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <PayableStatusBadge status={data.status} />
            <span className="text-sm text-muted-foreground">
              Vencimento: {formatDueDate(data.dueDate)}
            </span>
          </div>

          {data.notes && (
            <div>
              <div className="text-xs text-muted-foreground">Observações</div>
              <p className="whitespace-pre-wrap break-words text-sm">{data.notes}</p>
            </div>
          )}

          <div>
            <h4 className="mb-2 text-sm font-semibold">Pagamentos</h4>
            {data.payments.length === 0 ? (
              <EmptyState
                compact
                icon={<Receipt size={22} />}
                title="Nenhuma baixa registrada"
                description="Cada pagamento parcial deste título aparece aqui, com data e forma."
              />
            ) : (
              <div className="space-y-2">
                {data.payments.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-lg border px-3 py-2"
                  >
                    <div>
                      <div className="text-sm font-medium">{formatReais(p.amount)}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDueDate(p.paidAt)}
                        {p.method && ` · ${PAYABLE_METHOD_LABELS[p.method]}`}
                        {p.notes && ` · ${p.notes}`}
                      </div>
                    </div>
                    {!readOnly && (
                    <button
                      type="button"
                      disabled={isReversing}
                      onClick={async () => {
                        try {
                          await reversePayment({ id: data.id, paymentId: p.id });
                          await refetch();
                        } catch (err) {
                          toast({
                            type: 'error',
                            title: 'Falha ao estornar pagamento',
                            description: formatApiError(err, 'Tente novamente.'),
                          });
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-red-600 disabled:opacity-50 dark:hover:text-red-400"
                    >
                      <Undo2 size={12} />
                      Estornar
                    </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {!readOnly && (
          <div className="flex gap-3 border-t border-border pt-4">
            <button
              type="button"
              onClick={() => onEdit(data)}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Editar
            </button>
            {data.status !== 'paid' && (
              <button
                type="button"
                onClick={() => onPay(data)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                Registrar pagamento
              </button>
            )}
          </div>
          )}
        </div>
      )}
    </SidePanel>
  );
}
